import Config from 'config/Config';

const loadImageFromBlob = async (blob: Blob): Promise<HTMLImageElement | null> => {
  return new Promise((resolve) => {
    const objectUrl = URL.createObjectURL(blob);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(null);
    };
    image.src = objectUrl;
  });
};

const getOutputFileName = (fileName: string, mimeType: string) => {
  const ext = mimeType === 'image/png' ? 'png' : 'jpg';
  const dotIndex = fileName.lastIndexOf('.');
  if (dotIndex < 0) return `${fileName}.${ext}`;
  return `${fileName.substring(0, dotIndex)}.${ext}`;
};

const drawTopLeftCanvas = (image: HTMLImageElement, orientation: number) => {
  const width = image.width;
  const height = image.height;
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!context) {
    return null;
  }

  if (orientation >= 5 && orientation <= 8) {
    canvas.width = height;
    canvas.height = width;
  } else {
    canvas.width = width;
    canvas.height = height;
  }

  switch (orientation) {
    case 2:
      context.transform(-1, 0, 0, 1, width, 0);
      break;
    case 3:
      context.transform(-1, 0, 0, -1, width, height);
      break;
    case 4:
      context.transform(1, 0, 0, -1, 0, height);
      break;
    case 5:
      context.transform(0, 1, 1, 0, 0, 0);
      break;
    case 6:
      context.transform(0, 1, -1, 0, height, 0);
      break;
    case 7:
      context.transform(0, -1, -1, 0, height, width);
      break;
    case 8:
      context.transform(0, -1, 1, 0, 0, width);
      break;
    default:
      break;
  }

  context.drawImage(image, 0, 0);
  return canvas;
};

const resolveOutputMimeType = (mimeType: string) => {
  return mimeType === 'image/png' ? 'image/png' : 'image/jpeg';
};

const canvasToBlob = async (
  canvas: HTMLCanvasElement,
  mimeType: string,
  jpegQuality = Config.imageUpload.jpegQuality,
): Promise<Blob | null> => {
  return new Promise((resolve) => {
    const quality = mimeType === 'image/jpeg' ? jpegQuality : undefined;
    canvas.toBlob(
      (blob) => {
        resolve(blob);
      },
      mimeType,
      quality,
    );
  });
};

const getTopLeftImageInternal = async (file: File): Promise<HTMLImageElement | null> => {
  const image = await loadImageFromBlob(file);
  if (!image) {
    return null;
  }

  return image;
};

const getTopLeftImage = async (file: File): Promise<HTMLImageElement | null> => {
  const image = await getTopLeftImageInternal(file);
  return image;
};

const resizeCanvas = (canvas: HTMLCanvasElement, maxLongSide: number) => {
  if (maxLongSide <= 0) return canvas;

  const width = canvas.width;
  const height = canvas.height;

  if (width <= maxLongSide && height <= maxLongSide) {
    return canvas;
  }

  const ratio = Math.min(maxLongSide / width, maxLongSide / height);
  const resizedCanvas = document.createElement('canvas');
  resizedCanvas.width = Math.round(width * ratio);
  resizedCanvas.height = Math.round(height * ratio);

  const context = resizedCanvas.getContext('2d');
  if (!context) {
    return canvas;
  }

  context.drawImage(canvas, 0, 0, resizedCanvas.width, resizedCanvas.height);
  return resizedCanvas;
};

const getTopLeftResizedFile = async (
  file: File,
  maxLongSide: number,
  mimeType?: 'image/jpeg' | 'image/png',
  jpegQuality = Config.imageUpload.jpegQuality,
): Promise<File | null> => {
  const image = await loadImageFromBlob(file);
  if (!image) {
    return null;
  }

  // Policy: trust browser decoder behavior. Always skip manual EXIF transform.
  const orientationForCanvas = 1;

  const topLeftCanvas = drawTopLeftCanvas(image, orientationForCanvas);
  if (!topLeftCanvas) {
    return null;
  }

  const resizedCanvas = resizeCanvas(topLeftCanvas, maxLongSide);
  const outputMimeType = mimeType || resolveOutputMimeType(file.type);
  const blob = await canvasToBlob(resizedCanvas, outputMimeType, jpegQuality);
  if (!blob) {
    return null;
  }

  const outputFile = new File([blob], getOutputFileName(file.name, outputMimeType), {
    type: blob.type || outputMimeType,
    lastModified: file.lastModified,
  });

  return outputFile;
};

export { getTopLeftImage, getTopLeftResizedFile };
